/**
 * Estoque — helpers PUROS (sem I/O) do módulo de controle de estoque.
 *
 * Regra que define a feature: estoque só existe para PRODUTOS FÍSICOS
 * (`type='product'`), nunca serviços; e só quando o rastreio está ligado
 * (`track_stock`). O saldo é FRACIONÁRIO (kg/litro/m²).
 *
 * Estas funções são a superfície testável do módulo — a fonte de verdade do
 * saldo é o servidor (RPC apply_stock_movement); aqui só derivamos/formatamos.
 */
import type { Product, StockReason } from '@/types/database';

/** Rótulos pt-BR de cada motivo de movimento. */
export const STOCK_REASON_LABELS: Record<StockReason, string> = {
  manual_in: 'Entrada manual',
  manual_out: 'Saída manual',
  adjustment: 'Ajuste de estoque',
  quote_sale: 'Baixa por orçamento',
  service_order_sale: 'Baixa por ordem de serviço',
  purchase: 'Entrada por compra',
  sale_return: 'Devolução ao estoque',
};

/**
 * Motivos que o usuário escolhe na tela (movimento manual). quote_sale e
 * service_order_sale são definidos só programaticamente (prompt de baixa).
 */
export const MANUAL_STOCK_REASONS: { value: StockReason; label: string; direction: 'in' | 'out' | 'set' }[] = [
  { value: 'manual_in', label: 'Entrada', direction: 'in' },
  { value: 'manual_out', label: 'Saída', direction: 'out' },
  { value: 'purchase', label: 'Compra', direction: 'in' },
  { value: 'adjustment', label: 'Ajuste (contagem)', direction: 'set' },
];

export function reasonLabel(reason: StockReason): string {
  return STOCK_REASON_LABELS[reason] ?? reason;
}

/** Um produto é rastreável quando é físico (type='product') e tem rastreio ligado. */
export function isTrackedProduct(product: Pick<Product, 'type' | 'track_stock'>): boolean {
  return product.type === 'product' && product.track_stock === true;
}

/**
 * Campos mínimos para avaliar estoque baixo. Os dois são OPCIONAIS de propósito:
 * projeções mais frouxas (a linha crua do banco, as unidades do relatório de
 * estoque) precisam passar por aqui sem cast — e o corpo já trata ausência.
 */
export type LowStockInput = {
  stock_quantity?: number | null;
  min_stock?: number | null;
};

/**
 * Estoque baixo: só quando há um mínimo definido e o saldo é menor ou igual a
 * ele. Sem mínimo definido, nunca alerta (a coluna é opcional) — `min_stock`
 * nulo significa "sem alerta", NÃO zero.
 */
export function isLowStock(product: LowStockInput): boolean {
  if (product.min_stock == null) return false;
  return Number(product.stock_quantity ?? 0) <= Number(product.min_stock);
}

/** Filtra a lista de produtos rastreados que estão com estoque baixo. */
export function filterLowStock<T extends LowStockInput>(products: T[]): T[] {
  return products.filter((p) => isLowStock(p));
}

/**
 * Delta (assinado) de um movimento manual a partir de uma magnitude positiva.
 * Entrada/compra somam; saída subtrai. Para 'adjustment' NÃO use isto — use
 * adjustmentDelta (o ajuste define o saldo alvo, não uma magnitude).
 */
export function movementDelta(reason: StockReason, magnitude: number): number {
  const m = Math.abs(Number(magnitude) || 0);
  switch (reason) {
    case 'manual_in':
    case 'purchase':
      return m;
    case 'manual_out':
    case 'quote_sale':
    case 'service_order_sale':
      return -m;
    case 'sale_return':
      // Devolução SOMA: o produto volta para a prateleira.
      return m;
    case 'adjustment':
      // Ajuste é tratado à parte (adjustmentDelta); aqui devolvemos a magnitude
      // como está para não perder o sinal caso o chamador já o tenha embutido.
      return Number(magnitude) || 0;
  }
}

/**
 * Delta de um AJUSTE por contagem: leva o saldo atual até o saldo contado.
 * Ex.: atual 7, contado 5 → delta −2; atual 3, contado 8 → delta +5.
 */
export function adjustmentDelta(currentBalance: number, countedBalance: number): number {
  return Number(countedBalance ?? 0) - Number(currentBalance ?? 0);
}

/** Item genérico de baixa (vindo de quote_items / service_order_items). */
export interface DeductionSourceItem {
  product_id?: string | null;
  name?: string | null;
  quantity?: number | null;
  item_type?: string | null;
}

/** Linha de baixa consolidada por produto. */
export interface DeductionLine {
  product_id: string;
  name: string;
  quantity: number;
}

/**
 * A partir dos itens de um orçamento/OS e do conjunto de ids de produtos
 * RASTREADOS, monta as linhas de baixa: só itens cujo product_id está rastreado
 * e com quantidade positiva. Serviços e produtos sem rastreio são ignorados.
 * Consolida por product_id (o mesmo produto em duas linhas vira uma baixa só) e
 * ordena por nome.
 */
export function toDeductionLines(
  items: DeductionSourceItem[],
  trackedProductIds: Set<string>,
  nameById?: Map<string, string>,
): DeductionLine[] {
  const byProduct = new Map<string, DeductionLine>();

  for (const item of items ?? []) {
    const productId = item?.product_id ? String(item.product_id) : '';
    if (!productId || !trackedProductIds.has(productId)) continue;
    const qty = Number(item?.quantity ?? 0);
    if (!(qty > 0)) continue;

    const existing = byProduct.get(productId);
    if (existing) {
      existing.quantity += qty;
    } else {
      byProduct.set(productId, {
        product_id: productId,
        name: nameById?.get(productId) || (item?.name ? String(item.name) : 'Produto'),
        quantity: qty,
      });
    }
  }

  return Array.from(byProduct.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

/** Formata um saldo fracionário (pt-BR, até 3 casas, sem zeros à toa). */
export function formatStockQuantity(value: number): string {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 }).format(Number(value ?? 0));
}
